import { getMatchTypeLocalized } from "../../services/utils/utils";
import { formatMatchDate } from "../../utils/formatMatchDate";

import { TeamLogo } from "../shared/TeamLogo";
import { MatchGameGraphs } from "./MatchGameGraphs";
import { MatchGameView } from "./MatchGameView";
import { PageMeta } from "../../seo/PageMeta";
import { useMatchDetail } from "./hooks/useMatchDetail";

const pageClassName =
  "mx-auto flex w-full max-w-[var(--hero-max-width,80rem)] flex-col items-center gap-[clamp(1.25rem,2.5vw,2rem)] bg-page px-[var(--padding-view)] pt-[calc(2.5rem+13rem)] pb-[clamp(4rem,10vw,6rem)] [scroll-padding-top:calc(2.5rem+8rem)] max-md:pt-[calc(2rem+10rem)]";

const statusClassName = "m-0 font-jetbrains text-[clamp(1rem,1.5vw,1.1rem)]";

const matchDetailTabClassName = (isActive: boolean, isContext: boolean, extra = "") => {
  const state = isActive
    ? " match-detail-tab--active"
    : isContext
      ? " match-detail-tab--context"
      : "";
  return `match-detail-tab${state}${extra ? ` ${extra}` : ""}`;
};

const MatchDetailContent = () => {
  const {
    loading,
    error,
    detail,
    selectedGameIndex,
    setSelectedGameIndex,
    viewMode,
    setViewMode,
    games,
    playerStats,
    gameMappings,
    rosterByPuuid,
    seoTitle,
    seoDescription,
    matchHeadingId,
    copy,
    strings,
    locale,
    pathname,
    internalPath,
    teamInfos,
  } = useMatchDetail();

  if (loading) {
    return (
      <section className={pageClassName} aria-busy="true" aria-labelledby={matchHeadingId}>
        <PageMeta
          title={seoTitle}
          description={seoDescription}
          pathname={pathname}
          internalPath={internalPath}
        />
        <h1 id={matchHeadingId} className="visually-hidden">
          {seoTitle}
        </h1>
        <p className={statusClassName}>{copy.LOADING}</p>
      </section>
    );
  }

  if (error || !detail) {
    return (
      <section className={pageClassName} aria-labelledby={matchHeadingId}>
        <PageMeta
          title={seoTitle}
          description={seoDescription}
          pathname={pathname}
          internalPath={internalPath}
        />
        <h1 id={matchHeadingId} className="visually-hidden">
          {seoTitle}
        </h1>
        <p className={statusClassName} role="alert">
          {error ?? copy.NOT_FOUND}
        </p>
      </section>
    );
  }

  const { match } = detail;
  const when = formatMatchDate(match.time, locale, strings);
  const teamLogoAlt = (teamName: string) =>
    strings.COMMON.TEAM_LOGO_ALT.replace("{team}", teamName);

  return (
    <section className={pageClassName} aria-labelledby={matchHeadingId}>
      <PageMeta
        title={seoTitle}
        description={seoDescription}
        pathname={pathname}
        internalPath={internalPath}
      />
      <h1 id={matchHeadingId} className="visually-hidden">
        {match.teamOneName} vs {match.teamTwoName}
      </h1>
      <div className="flex w-full flex-col items-center gap-3 text-center">
        <p className="m-0 font-shentox text-[clamp(0.85rem,1.2vw,0.95rem)] tracking-[0.08em] uppercase opacity-75">
          {getMatchTypeLocalized(match.matchType, strings)}
        </p>
        {when && (
          <time
            className="font-jetbrains text-[clamp(0.9rem,1.3vw,1rem)] opacity-80"
            dateTime={when.dateTime}
          >
            {when.date}
          </time>
        )}

        <div className="mt-2 flex w-full flex-wrap items-center justify-center gap-[clamp(0.75rem,2vw,1.5rem)]">
          <div className="flex min-w-[min(100%,8rem)] flex-col items-center gap-2">
            <TeamLogo
              teamId={match.teamOne}
              alt={teamLogoAlt(match.teamOneName)}
              className="h-[clamp(3rem,8vw,4.5rem)] w-[clamp(3rem,8vw,4.5rem)] object-contain"
            />
            <span className="text-center font-shentox text-[clamp(0.95rem,1.5vw,1.15rem)] font-semibold">
              {match.teamOneName}
            </span>
          </div>
          <p className="match-detail-score">
            {match.teamOneScore} – {match.teamTwoScore}
          </p>
          <div className="flex min-w-[min(100%,8rem)] flex-col items-center gap-2">
            <TeamLogo
              teamId={match.teamTwo}
              alt={teamLogoAlt(match.teamTwoName)}
              className="h-[clamp(3rem,8vw,4.5rem)] w-[clamp(3rem,8vw,4.5rem)] object-contain"
            />
            <span className="text-center font-shentox text-[clamp(0.95rem,1.5vw,1.15rem)] font-semibold">
              {match.teamTwoName}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          {match.youtubeLink && (
            <a
              href={match.youtubeLink}
              className="match-vod-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {strings.SCHEDULE.WATCH_VOD}
            </a>
          )}
        </div>
      </div>

      {games.length > 0 ? (
        <div className="surface-card flex w-full flex-col items-stretch overflow-hidden">
          <nav
            className="border-b border-b-[var(--surface-card-border)] bg-white/[0.02] px-4 py-[0.85rem] max-md:p-3"
            aria-label={games.length > 1 ? copy.GAME_TABS : copy.GRAPHS}
          >
            <div className="flex w-full items-center gap-[0.3rem] rounded-round border border-[var(--surface-card-border)] bg-black/20 p-1">
              {games.length > 1 && (
                <div
                  className="inline-flex flex-row items-center gap-[0.3rem]"
                  role="tablist"
                  aria-orientation="horizontal"
                >
                  {games.map((game, index) => {
                    const isSelected = index === selectedGameIndex;
                    const isActive = viewMode === "scoreboard" && isSelected;
                    const isContext = viewMode === "graphs" && isSelected;
                    const tabLabel = copy.GAME_TAB.replace("{number}", String(index + 1));

                    return (
                      <button
                        key={game.game_id}
                        type="button"
                        role="tab"
                        id={`match-game-tab-${String(index)}`}
                        aria-selected={isActive}
                        aria-controls="match-detail-panel"
                        aria-label={tabLabel}
                        className={matchDetailTabClassName(isActive, isContext)}
                        onClick={() => {
                          setSelectedGameIndex(index);
                          if (viewMode !== "graphs") {
                            setViewMode("scoreboard");
                          }
                        }}
                      >
                        {index + 1}
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                role="tab"
                id="match-graphs-tab"
                aria-selected={viewMode === "graphs"}
                aria-controls="match-detail-panel"
                className={matchDetailTabClassName(
                  viewMode === "graphs",
                  false,
                  "match-detail-tab--graphs",
                )}
                onClick={() => {
                  setViewMode((mode) => (mode === "graphs" ? "scoreboard" : "graphs"));
                }}
              >
                {copy.GRAPHS}
              </button>
            </div>
          </nav>

          <div className="w-full min-w-0 flex-1 overflow-hidden">
            <div
              id="match-detail-panel"
              className="w-full"
              role="tabpanel"
              aria-labelledby={
                viewMode === "graphs"
                  ? "match-graphs-tab"
                  : games.length > 1
                    ? `match-game-tab-${String(selectedGameIndex)}`
                    : undefined
              }
            >
              {viewMode === "scoreboard" ? (
                <MatchGameView
                  game={games[selectedGameIndex]}
                  gameIndex={selectedGameIndex}
                  match={match}
                  teamInfos={teamInfos ?? []}
                  playerStats={playerStats}
                  mapping={gameMappings[selectedGameIndex]}
                  rosterByPuuid={rosterByPuuid}
                />
              ) : (
                <MatchGameGraphs
                  game={games[selectedGameIndex]}
                  gameIndex={selectedGameIndex}
                  rosterByPuuid={rosterByPuuid}
                  playerStats={playerStats}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="surface-card w-full max-w-[32rem] p-6 text-center">
          <p className={statusClassName}>{copy.NO_GAMES}</p>
          <p className="m-0 mt-2 font-jetbrains text-[0.9rem] opacity-75">{copy.NO_GAMES_HINT}</p>
        </div>
      )}
    </section>
  );
};

export const MatchDetailPage = () => <MatchDetailContent />;
